import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { useSnackbar } from "notistack";
import {
  community_feed_action,
  delete_feed_action,
  get_feeds_detail,
} from "src/DAL/Community/Community";
import { s3baseUrl } from "src/config/config";
import { htmlDecode } from "src/utils/convertHtml";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import {
  memberAsLiker,
  withLiker,
  withoutLiker,
} from "src/components/CommunityFeed/feedLikers";
import UserInfoCard from "src/components/CommunityFeed/UserInfoCard";
import FeedMoreMenu from "src/components/CommunityFeed/FeedMoreMenu";
import ActionsCount from "src/components/CommunityFeed/ActionsCount";
import HandleAction from "src/components/CommunityFeed/HandleAction";
import FeedComments from "src/components/CommunityFeed/FeedComments";
import FeedImageSlider from "src/components/CommunityFeed/FeedImageSlider";
import ShowAllLikes from "src/components/CommunityFeed/ShowAllLikes";
import ShowCoinsMembers from "src/components/CommunityFeed/ShowCoinsMembers";
import SendCoins from "src/components/CommunityFeed/SendCoins";
import CustomConfirmation from "src/components/GeneralComponents/CustomConfirmation";
import GrCard from "src/design-system/components/GrCard";
import GrLoader from "src/design-system/components/GrLoader";
import { gr } from "src/design-system/tokens";
import { feedPostSx } from "src/components/CommunityFeed/feedPostSx";
import CreatePost from "./CreatePost";

// One post opened on its own: the post popup (community feed, dashboard) and the post page
// (/community-area-post-detail/:id). Photos / video on the left on a tint pane, the post on the right
// (author + ⋮, text, counts, actions, comments with the comment box); one column for a text post.
// Below md everything stacks: media on top, the post under it.

const noop = () => {};

// A post shows the media pane only when it really has photos or a video link.
export const feedHasMedia = (feed) =>
  (feed?.feed_type === "image" && feed.image?.length > 0) ||
  (feed?.feed_type === "video" && Boolean(feed.video_url));

// Comments list in the popup: scrolls between the post and the comment box (TASK-079).
const COMMENTS_MIN_HEIGHT = 160;
// Large photo: the viewport minus the top bar / dialog margins and the thumbnail row.
const PHOTO_MAX_HEIGHT = `calc(100vh - ${gr.layout.topBar} * 3)`;
const PHOTO_MAX_HEIGHT_PHONE = "55vh";
const MEDIA_COLUMNS = `minmax(0, 1fr) ${gr.layout.drawer}`;

const headerSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: gr.space[3],
};
// The popup's close × sits in the top-right corner: keep the ⋮ clear of it.
const popupHeaderSx = { ...headerSx, pr: gr.space[10] };

const panelBaseSx = {
  ...feedPostSx,
  "--feed-post-pad": gr.space[6],
  "@media (max-width: 599.95px)": { "--feed-post-pad": gr.space[5] },
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  padding: "var(--feed-post-pad)",
  color: gr.color.ink,
  "& > *": { flexShrink: 0 },
};

// Popup, text post: the panel fills the dialog; comments scroll, the comment box stays at the bottom.
const popupPanelSx = {
  ...panelBaseSx,
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  "& > .profile-main-comments": {
    flex: "1 1 auto",
    minHeight: COMMENTS_MIN_HEIGHT,
    overflowY: "auto",
  },
};
// Popup with media: the right column scrolls on its own; stacked (below md) the whole sheet scrolls.
const popupMediaPanelSx = {
  ...panelBaseSx,
  height: { md: "100%" },
  overflowY: { md: "auto" },
  "& > .profile-main-comments": {
    flex: "1 1 auto",
    minHeight: { xs: 0, md: COMMENTS_MIN_HEIGHT },
    overflowY: { md: "auto" },
  },
};
// Page: the window scrolls; the panel takes its natural height.
const pagePanelSx = panelBaseSx;

const popupSplitSx = {
  flex: "1 1 auto",
  minHeight: 0,
  height: "100%",
  overflowY: { xs: "auto", md: "hidden" },
  display: { xs: "block", md: "grid" },
  gridTemplateColumns: MEDIA_COLUMNS,
};
const pageSplitSx = {
  display: { xs: "block", md: "grid" },
  gridTemplateColumns: MEDIA_COLUMNS,
};

const popupMediaSx = {
  minWidth: 0,
  minHeight: 0,
  height: { md: "100%" },
  overflow: { md: "hidden" },
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  // Dark behind the photo / video in the full-window viewer, so the media stands out.
  background: gr.color.ink,
};
const pageMediaSx = { minWidth: 0, background: gr.color.tint };

// Media content: photo slider (react-image-gallery) or video, restyled from the legacy slider look.
const mediaInnerSx = {
  width: "100%",
  minWidth: 0,
  p: { xs: gr.space[3], md: gr.space[4] },
  "& .image-slider": { width: "100%", margin: "0 !important" },
  "& .image-slider img[data-failed]": { visibility: "hidden" },
  "& .image-gallery-content .image-gallery-slide .image-gallery-image": {
    maxHeight: { xs: PHOTO_MAX_HEIGHT_PHONE, md: PHOTO_MAX_HEIGHT },
  },
  "& .image-gallery-thumbnails-wrapper": { mt: gr.space[2] },
  "& .image-gallery-thumbnail": {
    borderRadius: gr.radius.chip,
    overflow: "hidden",
    borderColor: "transparent",
  },
  "& .image-gallery-thumbnail.active, & .image-gallery-thumbnail:focus, & .image-gallery-thumbnail:hover":
    { borderColor: gr.color.brand },
  "& .image-slider .image-gallery-icon": {
    color: gr.color.ink2,
    filter: "none",
    "&:hover": { color: gr.color.brandDark },
    "&:focus": { outline: "none" },
    "&:focus-visible svg": { boxShadow: gr.shadow.focus },
  },
  "& .image-slider .image-gallery-icon svg": {
    width: gr.control.md,
    height: gr.control.md,
    padding: gr.space[2],
    color: "inherit",
    background: gr.color.surface,
    borderRadius: gr.radius.pill,
    boxShadow: gr.shadow.raised,
  },
};
// Page: the media stays in view while the post and its comments scroll by.
const pageMediaInnerSx = {
  ...mediaInnerSx,
  position: { md: "sticky" },
  top: `calc(${gr.layout.topBar} + ${gr.space[4]})`,
};

// Page card: no own padding (the media runs to the card edge); `clip` keeps the sticky media working.
const pageCardSx = { padding: 0, overflow: "clip" };
const pageCardTextSx = { ...pageCardSx, maxWidth: gr.layout.feed };

export default function FeedDetailBody({
  postId,
  initialFeed,
  layout = "popup",
  selectedImageIndex = 0,
  VideoPlayer,
  onFeedChange = noop,
  onDeleted,
  onUpdated,
  focusComposerOnComment = false,
}) {
  const { userInfo } = useContentSetting();
  const { enqueueSnackbar } = useSnackbar();
  const panelRef = useRef(null);
  const [feedDetail, setFeedDetail] = useState(initialFeed || null);
  const [isLoading, setIsLoading] = useState(!initialFeed);
  const [isLiked, setIsLiked] = useState(Boolean(initialFeed?.liked_by_me));
  const [likeCount, setLikeCount] = useState(
    initialFeed?.liked_by?.length || 0
  );
  const [feedType, setFeedType] = useState(initialFeed?.feed_type || "general");
  const [formAction, setFormAction] = useState("EDIT");
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openLikesModal, setOpenLikesModal] = useState(false);
  const [openCoinsModal, setOpenCoinsModal] = useState(false);
  const [sendCoins, setSendCoins] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  const refreshFeed = async () => {
    const result = await get_feeds_detail(postId);
    if (result.code === 200) {
      setFeedDetail(result.feed);
      setIsLiked(result.feed.liked_by_me);
      setLikeCount(result.feed.liked_by?.length);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
    setIsLoading(false);
  };

  // Fetch the full post once per post.
  useEffect(() => {
    refreshFeed();
  }, [postId]);

  const images = useMemo(
    () =>
      (feedDetail?.image || []).map((photo) => ({
        original: s3baseUrl + photo.thumbnail_1,
        thumbnail: s3baseUrl + photo.thumbnail_1,
      })),
    [feedDetail?.image]
  );

  // Same as on the post card: heart and count change at once, a second tap while saving is ignored.
  const likeSaving = useRef(false);
  const handleFeedAction = async (id, action) => {
    if (likeSaving.current) return;
    likeSaving.current = true;
    const like = action === "feedlike";
    const me = memberAsLiker(userInfo);
    const likersBefore = feedDetail?.liked_by;
    setIsLiked(like);
    setLikeCount((count) => Math.max(0, count + (like ? 1 : -1)));
    setFeedDetail((feed) => ({
      ...feed,
      liked_by: like
        ? withLiker(feed?.liked_by, me)
        : withoutLiker(feed?.liked_by, me),
    }));
    const result = await community_feed_action(id, like ? "like" : "unlike");
    likeSaving.current = false;
    if (result.code === 200) {
      setLikeCount(result.feed.liked_by?.length);
      refreshFeed();
      onFeedChange(id);
    } else {
      setIsLiked(!like);
      setLikeCount((count) => Math.max(0, count + (like ? -1 : 1)));
      setFeedDetail((feed) => ({ ...feed, liked_by: likersBefore }));
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const handleFeedEdit = () => {
    setFormAction("EDIT");
    setOpenCreateModal(true);
  };

  const handleAgreeDelete = async () => {
    setOpenDelete(false);
    const result = await delete_feed_action(feedDetail._id);
    if (result.code === 200) {
      enqueueSnackbar("Post deleted successfully", { variant: "success" });
      onDeleted(feedDetail);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  // "Comment" takes the member to the comment box (popup only, as before).
  const focusComposer = () =>
    panelRef.current?.querySelector("form textarea")?.focus();

  if (!feedDetail) return isLoading ? <GrLoader /> : null;

  const isPage = layout === "page";
  const hasMedia = feedHasMedia(feedDetail);

  const media = hasMedia ? (
    <Box sx={isPage ? pageMediaSx : popupMediaSx}>
      <Box sx={isPage ? pageMediaInnerSx : mediaInnerSx}>
        {feedDetail.feed_type === "image" ? (
          <FeedImageSlider data={images} selectedIndex={selectedImageIndex} />
        ) : (
          <VideoPlayer url={feedDetail.video_url} />
        )}
      </Box>
    </Box>
  ) : null;

  let panelSx = popupPanelSx;
  if (isPage) panelSx = pagePanelSx;
  else if (hasMedia) panelSx = popupMediaPanelSx;

  const panel = (
    <Box ref={panelRef} sx={panelSx}>
      {/* The popup's × sits over the header only for a text post; with media it is on the photo side. */}
      <Box sx={isPage || hasMedia ? headerSx : popupHeaderSx}>
        <UserInfoCard
          profile_image={s3baseUrl + feedDetail.created_by?.profile_image}
          user_name={htmlDecode(feedDetail.created_by?.name)}
          avatar_char={feedDetail.created_by?.name}
          date={feedDetail.createdAt}
          type={feedDetail.created_by?.user_type}
        />
        {feedDetail.is_self && (
          <FeedMoreMenu
            onEdit={handleFeedEdit}
            onDelete={() => setOpenDelete(true)}
          />
        )}
      </Box>
      <div className="post-description">
        <p dangerouslySetInnerHTML={{ __html: feedDetail.description }}></p>
      </div>
      <ActionsCount
        handleOpenLikedBox={() => setOpenLikesModal(true)}
        handleOpenCoinsBox={() => setOpenCoinsModal(true)}
        likeCount={likeCount}
        coinsCount={feedDetail.feed_points?.length}
        commentCount={feedDetail.total_comments}
        topLikedUser={feedDetail.liked_by}
        topCoinedUser={feedDetail.feed_points}
      />
      <hr />
      <HandleAction
        isLiked={isLiked}
        handleFeedAction={handleFeedAction}
        setShowAddComment={focusComposerOnComment ? focusComposer : noop}
        feed_id={postId}
        feeds_list={feedDetail}
        setSendCoins={setSendCoins}
        sendCoins={sendCoins}
      />
      <hr />
      <FeedComments
        is_popup
        feed_id={postId}
        getFeedsListingOnAddFeed={onFeedChange}
        getFeedsDeteil={refreshFeed}
      />
    </Box>
  );

  const content = hasMedia ? (
    <Box sx={isPage ? pageSplitSx : popupSplitSx}>
      {media}
      {panel}
    </Box>
  ) : (
    panel
  );

  return (
    <>
      {isPage ? (
        <GrCard
          as="article"
          padding="sm"
          sx={hasMedia ? pageCardSx : pageCardTextSx}
        >
          {content}
        </GrCard>
      ) : (
        content
      )}

      {openLikesModal && (
        <ShowAllLikes
          openLikesModal={openLikesModal}
          handleCloseSimpleBox={() => setOpenLikesModal(false)}
          feeds_list={feedDetail}
        />
      )}
      {openCoinsModal && (
        <ShowCoinsMembers
          openLikesModal={openCoinsModal}
          handleCloseSimpleBox={() => setOpenCoinsModal(false)}
          feeds_list={feedDetail}
        />
      )}
      {sendCoins && (
        <SendCoins
          sendCoins={sendCoins}
          handleCloseSimpleBox={() => setSendCoins(false)}
          feeds_list={feedDetail}
          handleUpdateSpecificFeed={refreshFeed}
        />
      )}
      <CustomConfirmation
        open={openDelete}
        setOpen={setOpenDelete}
        title={"Are you sure you want to delete this post?"}
        handleAgree={handleAgreeDelete}
      />
      <CreatePost
        setFeedType={setFeedType}
        feedType={feedType}
        openCreateModal={openCreateModal}
        setOpenCreateModal={setOpenCreateModal}
        getFeedsListing={onFeedChange}
        userInfo={userInfo}
        selectedFeed={feedDetail}
        formAction={formAction}
        setFormAction={setFormAction}
        handleSuccessUpdate={onUpdated || refreshFeed}
      />
    </>
  );
}

FeedDetailBody.propTypes = {
  postId: PropTypes.string.isRequired,
  // The post as the list already has it (popup): shown at once, then refreshed.
  initialFeed: PropTypes.object,
  layout: PropTypes.oneOf(["popup", "page"]),
  selectedImageIndex: PropTypes.number,
  VideoPlayer: PropTypes.elementType.isRequired,
  // Called with the post id after a like / comment / edit, so the list behind can refresh that post.
  onFeedChange: PropTypes.func,
  onDeleted: PropTypes.func.isRequired,
  // After an edit is saved; default: reload this post.
  onUpdated: PropTypes.func,
  focusComposerOnComment: PropTypes.bool,
};
